import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import './FormEditor.css';

interface FormEditorProps {
  content: string;
  formName: string;
  formId?: string;
  onContentChange?: (html: string) => void;
  onAIEditRequest?: (selection: string, instruction: string) => void;
  onDownload?: () => void;
  isLoading?: boolean;
  isDownloadReady?: boolean;
}

const FormEditor: React.FC<FormEditorProps> = ({
  content,
  formName,
  formId,
  onContentChange,
  onAIEditRequest,
  onDownload,
  isLoading = false,
  isDownloadReady = false
}) => {
  // AI 수정 인라인 UI 상태
  const [aiEditOpen, setAiEditOpen] = useState(false);
  const [aiEditInstruction, setAiEditInstruction] = useState('');
  const [aiEditSelection, setAiEditSelection] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const aiEditInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Table.configure({
        resizable: true,
        cellMinWidth: 25,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({
        placeholder: '서식 내용이 여기에 표시됩니다...',
      }),
    ],
    content,
    editable: !isLoading,
    onUpdate: ({ editor }) => {
      if (onContentChange) {
        onContentChange(editor.getHTML());
      }
    },
  });

  // content가 변경되면 에디터 업데이트
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // isLoading 변경 시 editable 동기화
  useEffect(() => {
    if (editor) {
      editor.setEditable(!isLoading);
    }
  }, [isLoading, editor]);

  // AI 수정 패널 열릴 때 포커스
  useEffect(() => {
    if (aiEditOpen && aiEditInputRef.current) {
      aiEditInputRef.current.focus();
    }
  }, [aiEditOpen]);

  // AI 수정 요청 핸들러 - 인라인 UI로 변경
  const handleAIEditClick = useCallback(() => {
    if (!editor || !onAIEditRequest) return;

    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ');

    if (selectedText.trim()) {
      setAiEditSelection(selectedText);
      setAiEditInstruction('');
      setSelectionError('');
      setAiEditOpen(true);
    } else {
      setSelectionError('수정할 텍스트를 먼저 선택해주세요.');
      setTimeout(() => setSelectionError(''), 3000);
    }
  }, [editor, onAIEditRequest]);

  // AI 수정 실행
  const handleAIEditSubmit = useCallback(() => {
    if (!aiEditInstruction.trim() || !aiEditSelection || !onAIEditRequest) return;
    onAIEditRequest(aiEditSelection, aiEditInstruction);
    setAiEditOpen(false);
    setAiEditInstruction('');
    setAiEditSelection('');
  }, [aiEditInstruction, aiEditSelection, onAIEditRequest]);

  // AI 수정 취소
  const handleAIEditCancel = useCallback(() => {
    setAiEditOpen(false);
    setAiEditInstruction('');
    setAiEditSelection('');
  }, []);

  // 미리보기: 새 창에서 현재 에디터 내용을 인쇄용으로 표시
  const handlePreview = useCallback(() => {
    if (!editor) return;
    const html = editor.getHTML();
    const previewWindow = window.open('', '_blank', 'width=800,height=1000');
    if (!previewWindow) return;
    previewWindow.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${formName || '서식'} - 미리보기</title>
<link href="https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;600;700&display=swap" rel="stylesheet">
<style>
  body { font-family: 'Noto Serif KR', 'Batang', serif;
    max-width: 210mm; margin: 20px auto; padding: 30px 40px; font-size: 14px; line-height: 1.8; color: #1a1a1a; }
  table { border-collapse: collapse; width: 100%; margin: 12px 0; border: 1px solid #666; }
  td, th { border: 1px solid #bbb; padding: 6px 10px; font-size: 13px; line-height: 1.5; }
  th { font-weight: 600; background: #f8f8f8; text-align: center; }
  h1, h2, h3 { font-family: 'Pretendard', 'Malgun Gothic', sans-serif; }
  h1 { font-size: 24px; } h2 { font-size: 20px; } h3 { font-size: 17px; }
  code { background: #fef3c7; color: #92400e; padding: 1px 6px; border-radius: 3px; border: 1px dashed #f59e0b; }
  @media print { body { margin: 0; padding: 20mm; } }
</style></head><body>${html}</body></html>`);
    previewWindow.document.close();
  }, [editor, formName]);

  if (!editor) {
    return (
      <div className="form-editor-loading">
        <div className="loading-spinner"></div>
        <span>에디터 로딩 중...</span>
      </div>
    );
  }

  return (
    <div className="form-editor-container">
      {/* 헤더 - 서식 이름 및 도구 */}
      <div className="form-editor-header">
        <h2 className="form-title">{formName || '서식'}</h2>
        <div className="form-editor-toolbar">
          <button
            className="toolbar-btn"
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={isLoading}
            title="굵게"
          >
            <strong>B</strong>
          </button>
          <button
            className="toolbar-btn"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={isLoading}
            title="기울임"
          >
            <em>I</em>
          </button>
          <div className="toolbar-divider" />
          <button
            className="toolbar-btn ai-btn"
            onClick={handleAIEditClick}
            disabled={isLoading || !onAIEditRequest}
            title="텍스트를 선택한 후 AI로 수정"
          >
            AI 수정
          </button>
          {selectionError && (
            <span className="selection-error">{selectionError}</span>
          )}
        </div>
      </div>

      {/* AI 수정 인라인 패널 */}
      {aiEditOpen && (
        <div className="ai-edit-panel">
          <div className="ai-edit-selection">
            <span className="ai-edit-label">선택 텍스트:</span>
            <span className="ai-edit-text">
              "{aiEditSelection.length > 80 ? aiEditSelection.substring(0, 80) + '...' : aiEditSelection}"
            </span>
          </div>
          <div className="ai-edit-input-row">
            <input
              ref={aiEditInputRef}
              type="text"
              className="ai-edit-input"
              value={aiEditInstruction}
              onChange={e => setAiEditInstruction(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleAIEditSubmit();
                if (e.key === 'Escape') handleAIEditCancel();
              }}
              placeholder="수정 지시를 입력하세요... (예: 이름을 홍길동으로 변경)"
              disabled={isLoading}
            />
            <button
              className="ai-edit-submit"
              onClick={handleAIEditSubmit}
              disabled={isLoading || !aiEditInstruction.trim()}
            >
              수정
            </button>
            <button
              className="ai-edit-cancel"
              onClick={handleAIEditCancel}
              disabled={isLoading}
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/* 에디터 본문 */}
      <div className={`form-editor-content ${isLoading ? 'loading' : ''}`}>
        <EditorContent editor={editor} />
        {isLoading && (
          <div className="editor-overlay">
            <div className="loading-spinner"></div>
            <span>AI 처리 중...</span>
          </div>
        )}
      </div>

      {/* 푸터 - 액션 버튼 */}
      <div className="form-editor-footer">
        <div className="footer-info">
          <span className="char-count">
            {editor.getText().length} 자
          </span>
          {formId && <span className="form-id">서식: {formId}</span>}
        </div>
        <div className="footer-actions">
          <button
            className="action-btn secondary"
            disabled={isLoading}
            onClick={handlePreview}
          >
            미리보기
          </button>
          <button
            className={`action-btn primary${isDownloadReady ? ' download-ready' : ''}`}
            disabled={isLoading}
            onClick={onDownload}
          >
            {isLoading ? '생성 중...' : (isDownloadReady ? 'HWP 다운로드' : 'HWP 생성')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormEditor;
